import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { isPushForUser } from "@speakai/shared";
import { useAuth } from "@/hooks/use-auth";
import { syncThisBrowser, turnOffOnThisBrowser } from "@/lib/meeting-reminders/browser-reminders";
import { isFirebaseConfigured } from "@/lib/meeting-reminders/firebase-messaging";
import {
  REMINDER_MESSAGE_SHOW,
  parseMeetingReminder,
  type MeetingReminder,
} from "@/lib/meeting-reminders/reminder-payload";
import { parseEntityPush, type EntityPush } from "@/lib/meeting-reminders/entity-push";
import { trackActivity } from "@/lib/integrations/activity-events";
import { MeetingReminderCard } from "./meeting-reminder-card";
import { PushNotificationCard } from "./push-notification-card";
import { BlockedNoticeCard } from "@/features/subscription/components/blocked-notice-card";

const MEETING_REMINDER_SURFACE = "web";

export function MeetingReminderHost() {
  const { user } = useAuth();
  const userId = user?._id;
  const available = isFirebaseConfigured();
  const navigate = useNavigate();
  const [reminders, setReminders] = useState<MeetingReminder[]>([]);
  const [pushes, setPushes] = useState<EntityPush[]>([]);

  useEffect(() => {
    if (!userId) return;
    // Firebase not configured in this build: fail closed and remove any leftover subscription.
    if (!available) {
      void turnOffOnThisBrowser();
      return;
    }
    // Re-sync when the connection returns, since a failed register or deactivate may be waiting.
    const sync = () => void syncThisBrowser(userId);
    sync();
    window.addEventListener("online", sync);
    return () => window.removeEventListener("online", sync);
  }, [userId, available]);

  useEffect(() => {
    if (!userId || !("serviceWorker" in navigator)) return;
    function onMessage(event: MessageEvent) {
      // Same recipient check as the worker, in case the account changed after the push arrived.
      if (event.data?.type === REMINDER_MESSAGE_SHOW && isPushForUser(event.data.data, userId as string)) {
        const reminder = parseMeetingReminder(event.data.data);
        const push = reminder ? null : parseEntityPush(event.data.data);
        if (reminder) {
          setReminders((current) => [...current.filter((item) => item.eventId !== reminder.eventId), reminder]);
          trackActivity("meeting_reminder_shown", { surface: MEETING_REMINDER_SURFACE });
          // The ack tells the worker not to also show a system notification.
          event.ports[0]?.postMessage("ack");
        } else if (push) {
          setPushes((current) => [...current.filter((item) => item.tag !== push.tag), push]);
          event.ports[0]?.postMessage("ack");
        }
      }
    }
    navigator.serviceWorker.addEventListener("message", onMessage);
    return () => navigator.serviceWorker.removeEventListener("message", onMessage);
  }, [userId]);

  const dismissPush = useCallback((tag: string) => setPushes((current) => current.filter((item) => item.tag !== tag)), []);

  const openPush = useCallback(
    (push: EntityPush) => {
      navigate(push.path);
      dismissPush(push.tag);
    },
    [navigate, dismissPush],
  );

  const dismiss = useCallback((eventId: string) => setReminders((current) => current.filter((item) => item.eventId !== eventId)), []);

  return (
    <div
      aria-live="polite"
      className="pointer-events-none fixed inset-x-4 bottom-4 z-50 flex flex-col gap-2 sm:left-auto sm:right-4 sm:w-96"
    >
      {reminders.map((reminder) => (
        <MeetingReminderCard
          key={reminder.eventId}
          reminder={reminder}
          onDismiss={dismiss}
          onJoin={(url) => {
            trackActivity("meeting_reminder_join", { surface: MEETING_REMINDER_SURFACE });
            window.open(url, "_blank", "noopener,noreferrer");
            dismiss(reminder.eventId);
          }}
        />
      ))}
      {pushes.map((push) => (
        <PushNotificationCard key={push.tag} push={push} onOpen={openPush} onDismiss={dismissPush} />
      ))}
      <BlockedNoticeCard />
    </div>
  );
}
